import errorWarning from "$assets/icons/error-warning.svg?url";
import { Button } from "$components/button/button";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import { toast } from "$global-providers/toast-provider";
import { Box, makeStyles, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { RxDocument } from "rxdb";

const useStyles = makeStyles((theme) => ({
  warningIcon: { marginRight: theme.spacing(2) },
}));

/**
 * V2 delete confirmation. Soft-delete by setting `meta.deleted = true`;
 * the rxdb replication module maps this to `_deleted = true` on push,
 * which the backend turns into a real delete. No apollo mutation, no
 * Apollo cache evict step (rxdb's reactive query updates the list
 * automatically once the local doc flips).
 */
export const DeleteInvoiceModalV2 = ({
  doc,
  open,
  onClose,
}: {
  doc: RxDocument<RxInvoice>;
  open: boolean;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    try {
      // Only an rxdb-level remove() replicates as a deletion — the push
      // mapper reads `_deleted`, not doc data.
      await doc.getLatest().remove();
    } catch {
      toast.error(
        formatMessage({
          id: "invoicing.deleteInvoice.error.msg",
          defaultMessage: "Failed to delete this invoice",
        }),
      );
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <DialogContainer open={open} onClose={onClose}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={errorWarning}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.delete.modal.subtitle"
              defaultMessage="Are you sure you want to delete this invoice?"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <DialogFooter>
        <Box>
          <Button color="secondary" onClick={onClose} disabled={busy}>
            <FormattedMessage
              id="invoicing.delete.cancel"
              defaultMessage="Cancel"
            />
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            data-testid="invoicing.delete.confirm"
            startIcon={<CheckIcon />}
            loading={busy}
          >
            <FormattedMessage
              id="invoicing.delete.confirm"
              defaultMessage="Delete Invoice"
            />
          </Button>
        </Box>
      </DialogFooter>
    </DialogContainer>
  );
};
